import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { hasCap, isTruck as isTruckRole } from "./roles";
import { fetchBranding, invalidateBranding } from "./company";

const AuthContext = createContext(null);

const TOKEN_KEY = "rescue_token";
const USER_KEY = "rescue_user";
const AUTH_ERROR_KEY = "rescue_auth_error";

// Signed-out devices must not keep the previous user's offline copies.
function clearOfflineReadCache() {
  localStorage.removeItem(USER_KEY);
  if (typeof caches !== "undefined") caches.delete("tow-api-v1").catch(() => {});
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  // Why the session ended, when it wasn't just "your token expired".
  const [authError, setAuthError] = useState(
    () => sessionStorage.getItem(AUTH_ERROR_KEY) || null
  );

  // attach interceptor once
  useEffect(() => {
    const reqId = api.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    const resId = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        // A suspended or closed account keeps returning valid-looking 403s.
        // Sign out with the reason rather than leaving them clicking a dead UI.
        if (
          status === 403 &&
          (code === "tenant_suspended" || code === "tenant_archived") &&
          localStorage.getItem(TOKEN_KEY)
        ) {
          localStorage.removeItem(TOKEN_KEY);
          clearOfflineReadCache();
          // Survives the redirect below, and survives the user refreshing
          // the login page afterwards.
          sessionStorage.setItem(
            AUTH_ERROR_KEY,
            detail?.msg || "This account is no longer active."
          );
          setAuthError(detail?.msg || "This account is no longer active.");
          setToken(null);
          setUser(false);
          // Hard redirect: the page they were on will otherwise fire its own
          // "failed to load" toasts as every in-flight request 403s too.
          if (window.location.pathname !== "/login") {
            window.location.replace("/login");
          }
          return Promise.reject(err);
        }
        if (status === 401 && localStorage.getItem(TOKEN_KEY)) {
          localStorage.removeItem(TOKEN_KEY);
          clearOfflineReadCache();
          setToken(null);
          setUser(false);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, []);

  const fetchMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      setUser(data);
    } catch (err) {
      // No signal: keep the driver signed in with their last-known profile
      // so offline photos/status updates still work after a reload.
      const cached = localStorage.getItem(USER_KEY);
      if (!err?.response && cached) {
        setUser(JSON.parse(cached));
        return;
      }
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(false);
    }
  }, []);
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);
  const login = async (email, password) => {
    setAuthError(null);
    sessionStorage.removeItem(AUTH_ERROR_KEY);
    const { data } = await api.post("/auth/login", { email, password });
    clearOfflineReadCache();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    // Branding is fetched unauthenticated on the login page, which resolves
    // to the legacy tenant. Drop that cache and refetch with this user's
    // token or the sidebar paints the WRONG company's name for up to 60s.
    invalidateBranding();
    try {
      await fetchBranding({ force: true });
    } catch {
      /* non-fatal — useBranding will retry */
    }
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    clearOfflineReadCache();
    invalidateBranding();
    setToken(null);
    setUser(false);
  };

  // Oct 2026 — role checks go through `lib/roles` so `owner` (the only
  // login a brand-new tenant has) satisfies admin + dispatcher, exactly
  // like the backend's `_role_satisfies`. Before this, a new tenant's
  // owner was treated as non-admin by every `isAdmin` consumer — which
  // locked them out of their own setup wizard on day one.
  const isAdmin = hasCap(user, "admin");
  const isTruck = isTruckRole(user);
  const isStaff = hasCap(user, "admin", "dispatcher");

  const value = useMemo(
    () => ({ user, token, login, logout, isAdmin, isTruck, isStaff, authError,
             refreshUser: fetchMe }),
    [user, token, isAdmin, isTruck, isStaff, authError, fetchMe]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
